import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import InfiniteScroll from "react-infinite-scroll-component";
import { CircularProgress } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";

import CaptureFrame from "../../assets/icons/CaptureFrame";
import GalleryHeader from "../../components/WheelGallery/Header/GalleryHeader";
import CreateWheelPopup from "../../components/CreateWheelPopup";
import DeleteModal from "../../components/DeleteModal";
import Button from "../../components/Button";
import TextView from "../../components/TextView";
import { colors } from "../../utils";
import { openModal } from "../../redux/reducers/dialog";
import { showCreateWheelPopup } from "../../redux/reducers/createWheelPopup";
import { toast } from "react-toastify";

import {
  useCaptureItems,
  useCaptureExport,
  useDeleteCaptureItem,
  useDeleteCaptureItems,
  useDetachCaptureItem,
} from "../../services/capture";
import { downloadExportManifest } from "./captureExport";
import { captureIdsFrom } from "../../services/captureSeed";
import {
  flattenCapturePages,
  isRetryBusy,
  filterCaptures,
  typeCountsForQuery,
  headerCheckboxState,
  toggleSelected,
  captureTypeLabel,
  detachConfirmCopy,
  attachedWheelsList,
  formatChipCount,
  isFiltering,
  shouldDrainForFilter,
  captureListPhase,
  CAPTURE_FILTER_TYPES,
  FILTER_ALL,
} from "./Capture.helpers";
import { ATTACH_DETACH_WHEELS_LABEL, ATTACH_WHEEL_MODAL_HEADING } from "./captureLabels";
import { deleteConfirmCopy } from "./captureDeleteConfirm";
import { captureEmptyState } from "./captureEmptyState";
import { captureEmptyStateIcon } from "./captureTypeIcon";
import { composerTypeForFilter } from "./captureComposerContext";
import {
  bulkDeleteConfirmCopy,
  bulkDeleteResultMessage,
} from "./captureBulkConfirm";
import CaptureCard from "./components/CaptureCard";
import AddCaptureModal from "./components/AddCaptureModal";
import AttachWheelModal from "./components/AttachWheelModal";
import DetachWheelModal from "./components/DetachWheelModal";
import DonutWheelIcon from "./components/DonutWheelIcon";
import {
  Page,
  Shell,
  TopBar,
  TitleGroup,
  CountBadge,
  AddButtonWrap,
  TopBarActions,
  ExportButtonWrap,
  List,
  StateWrap,
  EmptyArt,
  SkeletonCard,
  SkeletonBox,
  SentinelRow,
  Toolbar,
  ToolbarRow,
  SearchWrap,
  SearchInput,
  Chips,
  Chip,
  ChipCount,
  CbAll,
  BulkBar,
  BulkCount,
  BulkBtn,
  BulkClear,
} from "./styles";

const SCROLL_ID = "capture-scroll";

const LoadingState = () => (
  <List>
    {[...Array(5)].map((_, i) => (
      <SkeletonCard key={i}>
        <SkeletonBox style={{ width: 56, height: 56, borderRadius: 8 }} />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
          <SkeletonBox style={{ width: "30%", height: 12 }} />
          <SkeletonBox style={{ width: "80%", height: 14 }} />
        </div>
      </SkeletonCard>
    ))}
  </List>
);

const Capture = () => {
  const dispatch = useDispatch();

  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null); // CAP-3: the capture being edited, or null
  const [attachPicker, setAttachPicker] = useState(null); // { items, title } | null
  const [detachPickItem, setDetachPickItem] = useState(null);
  const [detachPickSelectAll, setDetachPickSelectAll] = useState(false);
  const [query, setQuery] = useState("");
  const [filterType, setFilterType] = useState(FILTER_ALL);
  /**
   * QA-78 killed "select mode": there is no longer a mode to be in. Checkboxes
   * are always on every card and the only selection state left is WHICH ids are
   * ticked. `selectMode`/`exitSelect`/the Select-Done toggle are gone, not
   * hidden — a mode nobody can enter is dead code (Nick #8).
   */
  const [selectedIds, setSelectedIds] = useState([]);

  const {
    data,
    isLoading,
    isError,
    isFetching,
    isRefetching,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCaptureItems();

  const deleteItem = useDeleteCaptureItem();
  const deleteItems = useDeleteCaptureItems(); // QA-76/79 bulk delete
  const detachItem = useDetachCaptureItem();
  const exportAll = useCaptureExport(); // C15.13 export-all

  // Export ALL (C15.13): fetch the whole inbox as one JSON manifest and hand
  // the user a downloadable CSV. `isLoading` on the mutation guards the button
  // against a double-submit while the request is in flight.
  const handleExportAll = () => {
    exportAll.mutate(undefined, {
      onSuccess: (payload) => {
        try {
          downloadExportManifest(payload);
          toast.success("CSV ready — check your downloads.");
        } catch (err) {
          console.error("Export download failed:", err);
          toast.error("Couldn't build the export file.");
        }
      },
      onError: () => toast.error("Export failed. Please try again."),
    });
  };

  const retryBusy = isRetryBusy({ isFetching, isRefetching, isLoading });

  const allItems = useMemo(() => flattenCapturePages(data), [data]);
  // Chip badges are scoped to the active search so the toolbar can never
  // contradict the list underneath it (a chip reading `Photo 12` above a
  // single search-filtered row). Type filtering is intentionally NOT applied
  // — see typeCountsForQuery.
  const counts = useMemo(() => typeCountsForQuery(allItems, query), [allItems, query]);
  const visibleItems = useMemo(
    () => filterCaptures(allItems, { query, type: filterType }),
    [allItems, query, filterType]
  );
  const totalCount = data?.pages?.[0]?.count ?? allItems.length;

  /**
   * Counts are computed client-side over the pages fetched so far, so they are
   * only TRUE once the inbox has been fully drained. Until then the toolbar
   * must not present a page-scoped number as the whole picture — see
   * formatChipCount / CHIP_COUNT_PARTIAL for the measured defect this fixes.
   */
  const countsComplete = !hasNextPage;
  const filtering = isFiltering({ query, type: filterType });

  /**
   * The "All" badge has an AUTHORITATIVE source when no search is narrowing it:
   * the DRF `count` the server already returns on page 1 (the same number the
   * header badge shows). Using it removes the screen's self-contradiction —
   * header "35" above a chip reading "20" — with no extra request.
   */
  const allChipCount = query
    ? formatChipCount(counts[FILTER_ALL], { complete: countsComplete })
    : String(totalCount);

  /**
   * Filtering DRAINS the remaining pages rather than filtering whatever happens
   * to be in memory. The backend list endpoint offers no type filter and no
   * per-type counts, so this is the only way a chip can answer honestly; it
   * also means tapping a chip performs a real request instead of
   * short-circuiting on a stale local count.
   */
  useEffect(() => {
    if (shouldDrainForFilter({ filtering, hasNextPage, isFetchingNextPage })) {
      fetchNextPage();
    }
  }, [filtering, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const selectedItems = useMemo(
    () => visibleItems.filter((it) => selectedIds.includes(it.id)),
    [visibleItems, selectedIds]
  );
  const headerState = headerCheckboxState(selectedItems.length, visibleItems.length);

  const clearSelection = () => setSelectedIds([]);

  /**
   * Open the composer (QA-101, web leg).
   *
   * Every entry point to "New capture" on this page goes through here so the
   * category context can never be inherited by one button and dropped by
   * another — the top-bar (+) and the inline empty-state CTA are the same call.
   */
  const openComposer = () => setShowAdd(true);

  const toggleSelectAll = () => {
    if (headerState === "all") setSelectedIds([]);
    else setSelectedIds(visibleItems.map((it) => it.id));
  };

  const handleDelete = (item) => {
    dispatch(
      openModal({
        ...deleteConfirmCopy(item),
        onConfirm: () => {
          deleteItem.mutate(item.id, {
            onSuccess: () => toast.success("Capture deleted."),
            onError: () => toast.error("Could not delete. Please try again."),
          });
        },
      })
    );
  };

  /**
   * Bulk delete the current selection (Gordon QA-76 + QA-79).
   *
   * Routed through the SAME shared confirm every other destructive action on
   * this page uses, with SCOPED copy (G-163): the dialog names the COUNT and
   * lists the items BEFORE anything is destroyed, so a user who selected more
   * than they meant to sees it in the dialog rather than in the aftermath. The
   * confirm is dispatched with a title a test can assert on before clicking —
   * never a page-wide text match on a bare "Delete".
   */
  const handleBulkDelete = () => {
    const items = selectedItems;
    if (items.length === 0) return;
    dispatch(
      openModal({
        ...bulkDeleteConfirmCopy(items),
        onConfirm: () => {
          deleteItems.mutate(
            items.map((it) => it.id),
            {
              onSuccess: (result) => {
                const { tone, message } = bulkDeleteResultMessage(result);
                // A partial failure is reported as partial, not as success.
                if (tone === "success") toast.success(message);
                else if (tone === "warning") toast.warning(message);
                else toast.error(message);
                clearSelection();
              },
              onError: () => toast.error("Could not delete. Please try again."),
            }
          );
        },
      })
    );
  };

  const openDetachPicker = (item, { selectAll = false } = {}) => {
    setDetachPickItem(item);
    setDetachPickSelectAll(selectAll);
  };

  const closeDetachPicker = () => {
    setDetachPickItem(null);
    setDetachPickSelectAll(false);
  };

  /**
   * Detach an attached capture from one or more Wheels (Kelly: "need to be able
   * to delete captured items on a wheel if we decide not to use them").
   *
   * M2M: `{ wheel_id: null }` would drop EVERY link. One wheel → confirm and
   * post that id to `/detach/`. Several wheels → checkbox picker, then
   * POST `/detach-wheels/` with `{ wheel_ids: [...] }`.
   */
  const confirmDetachFrom = (item, wheelOrWheels) => {
    const fromPicker = Array.isArray(wheelOrWheels);
    const wheels = (fromPicker ? wheelOrWheels : wheelOrWheels ? [wheelOrWheels] : [])
      .filter((wheel) => wheel && wheel.id != null);
    const names = wheels.map((wheel) => wheel.name).filter(Boolean);
    const wheelIds = wheels.map((wheel) => wheel.id);

    dispatch(
      openModal({
        ...detachConfirmCopy(names.length <= 1 ? names[0] : names),
        onConfirm: () => {
          const payload = fromPicker
            ? { id: item.id, wheelIds }
            : { id: item.id, wheelId: wheelIds[0] ?? null };
          detachItem.mutate(payload, {
            onSuccess: () => toast.success("Capture detached."),
            onError: () => toast.error("Could not detach. Please try again."),
          });
        },
      })
    );
  };

  const handleDetach = (item) => {
    const wheels = attachedWheelsList(item);
    if (wheels.length > 1) {
      openDetachPicker(item);
      return;
    }
    confirmDetachFrom(item, wheels[0] || null);
  };

  // "Create new Wheel" (deck 06): open the real Create-a-Wheel wizard (the
  // showCreateWheelPopup modal every other entry point in the app uses --
  // see WheelGallery/WheelListing/ListComponents/ListHeader.jsx). The old
  // navigate(PATH.CREATE_CUSTOM_WHEEL) target was never a registered route
  // and always 404'd.
  //
  // The selected capture(s) ride along: deck screen 06-create-new-confirm
  // specifies they land on the wheel being created, and shipping without that
  // meant a user could select three captures, press Create Wheel, and get a
  // wheel that referenced none of them (all three stayed attached_wheel: null).
  // The wizard attaches them with the EXISTING capture->wheel attach call once
  // the wheel actually exists -- a cancelled or failed create attaches nothing.
  // `source` is one capture item (row action menu), the selected array (bulk
  // bar), or nothing at all; captureIdsFrom normalizes all three.
  const handleCreateNew = (source) => {
    // promptAfterCreate: after Build succeeds, stay on Capture and ask before
    // hard-navigating to /builder (Gallery / Explore create leave this false).
    dispatch(
      showCreateWheelPopup({
        captureIds: captureIdsFrom(source),
        promptAfterCreate: true,
      })
    );
  };

  /**
   * The empty panel (QA-100, Osama).
   *
   * ONE renderer for all three empty shapes — whole-inbox, empty category, and
   * failed search — because the difference between them is a copy decision, and
   * that decision lives in `captureEmptyState` where it is unit-testable. The
   * purple "New capture" CTA renders INLINE on the category and whole-inbox
   * states (the useful next step is to create one) and is withheld on the search
   * state (creating a capture does not answer "nothing matched").
   */
  const renderEmptyState = ({ type, query: activeQuery }) => {
    const state = captureEmptyState({ type, query: activeQuery });
    // CAP-A (VISUAL-AUDIT 2026-08-11): a failed search shows the magnifier; an
    // empty CATEGORY shows that type's own line-art glyph (video/mic/doc/link/
    // note) instead of the hard-coded Note glyph, so the art matches the copy.
    const EmptyIcon =
      state.kind === "search" ? SearchRoundedIcon : captureEmptyStateIcon(type);
    return (
      <StateWrap data-testid="capture-empty-state" data-empty-kind={state.kind}>
        <EmptyArt>
          <EmptyIcon fontSize="large" />
        </EmptyArt>
        <TextView variant="subtitle" color={colors.black}>
          {state.title}
        </TextView>
        <TextView variant="largeText" color={colors.textGrey}>
          {state.body}
        </TextView>
        {state.showCta && (
          <AddButtonWrap>
            <Button onClick={openComposer}>
              <span style={{ marginRight: 4, display: "inline-flex", flexShrink: 0 }}>
                <CaptureFrame color={colors.white} width={20} height={20} strokeWidth={1.8} />
              </span>
              {state.ctaLabel}
            </Button>
          </AddButtonWrap>
        )}
      </StateWrap>
    );
  };

  const renderBody = () => {
    if (isLoading) return <LoadingState />;

    if (isError) {
      return (
        <StateWrap>
          <EmptyArt>
            <StickyNote2OutlinedIcon fontSize="large" />
          </EmptyArt>
          <TextView variant="subtitle" color={colors.black}>
            We couldn't load your captures
          </TextView>
          <TextView variant="largeText" color={colors.textGrey}>
            Something went wrong. Please try again.
          </TextView>
          <AddButtonWrap>
            {/* Busy state from react-query's refetch flag: immediate spinner +
                disabled so it can't re-fire; the query's retry predicate fires
                exactly one request on a 4xx (ISP-4068 C). */}
            <Button onClick={() => refetch()} isLoading={retryBusy} disabled={retryBusy}>
              Retry
            </Button>
          </AddButtonWrap>
        </StateWrap>
      );
    }

    if (allItems.length === 0) {
      return renderEmptyState({ type: FILTER_ALL, query: "" });
    }

    // 'searching' is the state that used to be reported as "No matching
    // captures": nothing has matched YET, but pages are still arriving, so the
    // app does not know that nothing matches and must not say so.
    const phase = captureListPhase({
      visibleCount: visibleItems.length,
      filtering,
      hasNextPage,
      isFetchingNextPage,
    });

    return (
      <>
        {renderToolbar()}

        {phase === "searching" ? (
          <StateWrap>
            <CircularProgress size={28} style={{ color: colors.PrimaryPurple500 }} />
            <TextView variant="subtitle" color={colors.black}>
              Looking through the rest of your inbox…
            </TextView>
            <TextView variant="largeText" color={colors.textGrey}>
              You have more captures than fit on one page. Checking them all
              before saying there's nothing here.
            </TextView>
          </StateWrap>
        ) : phase === "empty" ? (
          renderEmptyState({ type: filterType, query })
        ) : (
          <InfiniteScroll
            dataLength={visibleItems.length}
            next={fetchNextPage}
            /* Pagination is NOT suspended while a filter is active: the server
               cannot filter by type, so stopping here is what made a chip
               report 0 for a type living on page 2. */
            hasMore={!!hasNextPage}
            scrollableTarget={SCROLL_ID}
            style={{ overflow: "visible" }}
            loader={
              <SentinelRow>
                <CircularProgress size={22} style={{ color: colors.PrimaryPurple500 }} />
              </SentinelRow>
            }
            endMessage={
              <SentinelRow>
                <TextView variant="tag" color={colors.textGrey}>
                  You're all caught up.
                </TextView>
              </SentinelRow>
            }
          >
            <List>
              {visibleItems.map((item) => (
                <CaptureCard
                  key={item.id}
                  item={item}
                  selected={selectedIds.includes(item.id)}
                  onToggleSelect={(it) => setSelectedIds((ids) => toggleSelected(ids, it.id))}
                  onAttach={(it) =>
                    setAttachPicker({ items: [it], title: ATTACH_WHEEL_MODAL_HEADING })
                  }
                  onCreateNew={(it) => handleCreateNew(it)}
                  onEdit={(it) => setEditItem(it)}
                  onDelete={handleDelete}
                  onDetach={handleDetach}
                  isDeleting={deleteItem.isLoading}
                  isDetaching={detachItem.isLoading}
                />
              ))}
            </List>
            {isFetchingNextPage && (
              <SentinelRow>
                <CircularProgress size={22} style={{ color: colors.PrimaryPurple500 }} />
              </SentinelRow>
            )}
          </InfiniteScroll>
        )}
      </>
    );
  };

  const renderToolbar = () => (
    <Toolbar>
      <ToolbarRow>
        <SearchWrap>
          <SearchRoundedIcon fontSize="small" />
          <SearchInput
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search"
            aria-label="Search"
          />
        </SearchWrap>
        {/* QA-78 (1): the Select / Done toggle is GONE. There is no mode to
            enter or leave — the checkboxes are always there. */}
      </ToolbarRow>

      <Chips>
        {/* Always rendered, for the same reason: select-all is only reachable
            if it is visible without first entering a mode. */}
        <CbAll
          type="button"
          role="checkbox"
          aria-checked={headerState === "all" ? "true" : headerState === "some" ? "mixed" : "false"}
          aria-label="Select all captures"
          state={headerState}
          onClick={toggleSelectAll}
        >
          {headerState === "all" && <CheckRoundedIcon style={{ fontSize: 15 }} />}
          {headerState === "some" && <RemoveRoundedIcon style={{ fontSize: 15 }} />}
        </CbAll>

        <Chip
          type="button"
          active={filterType === FILTER_ALL}
          aria-pressed={filterType === FILTER_ALL}
          onClick={() => setFilterType(FILTER_ALL)}
        >
          All
          <ChipCount active={filterType === FILTER_ALL}>{allChipCount}</ChipCount>
        </Chip>
        {CAPTURE_FILTER_TYPES.map((t) => (
          <Chip
            key={t}
            type="button"
            active={filterType === t}
            aria-pressed={filterType === t}
            onClick={() => setFilterType(t)}
          >
            {captureTypeLabel(t)}
            {/* An unknown count renders as a marker, never as "0" — the chip
                stays tappable and tapping it fetches the rest of the inbox. */}
            <ChipCount
              active={filterType === t}
              title={
                countsComplete
                  ? undefined
                  : `At least ${counts[t]} — still counting the rest of your inbox`
              }
            >
              {formatChipCount(counts[t], { complete: countsComplete })}
            </ChipCount>
          </Chip>
        ))}
      </Chips>
    </Toolbar>
  );

  return (
    <Page id={SCROLL_ID}>
      <GalleryHeader />
      <Shell>
        <TopBar>
          <TitleGroup>
            <TextView variant="largeTitle" color={colors.primary}>
              Capture
            </TextView>
            {!isLoading && !isError && <CountBadge>{totalCount}</CountBadge>}
          </TitleGroup>
          <TopBarActions>
            {/* Export ALL (C15.13): available whenever the inbox has at least
                one item; disabled while empty or while a request is in flight
                (isLoading double-submit guard). */}
            <ExportButtonWrap>
              <Button
                secondary
                onClick={handleExportAll}
                isLoading={exportAll.isLoading}
                disabled={exportAll.isLoading || allItems.length === 0}
                aria-label="Export all captures"
              >
                <FileDownloadOutlinedIcon
                  fontSize="small"
                  style={{ marginRight: 4 }}
                />
                Export all
              </Button>
            </ExportButtonWrap>
            <AddButtonWrap>
              <Button onClick={openComposer}>
                <span style={{ marginRight: 4, display: "inline-flex", flexShrink: 0 }}>
                  <CaptureFrame color={colors.white} width={20} height={20} strokeWidth={1.8} />
                </span>
                New capture
              </Button>
            </AddButtonWrap>
          </TopBarActions>
        </TopBar>

        {renderBody()}
      </Shell>

      {/* No `selectMode` guard any more (QA-78): the bar appears the moment
          anything is ticked, because ticking IS the selection. */}
      {selectedItems.length > 0 && (
        <BulkBar role="region" aria-label="Bulk actions">
          <BulkCount>{selectedItems.length} selected</BulkCount>
          <BulkBtn
            type="button"
            primary
            onClick={() =>
              setAttachPicker({ items: selectedItems, title: ATTACH_WHEEL_MODAL_HEADING })
            }
          >
            <DonutWheelIcon size={16} color={colors.white} />
            {ATTACH_DETACH_WHEELS_LABEL}
          </BulkBtn>
          <BulkBtn type="button" onClick={() => handleCreateNew(selectedItems)}>
            <AddRoundedIcon fontSize="small" />
            Create Wheel
          </BulkBtn>
          {/* QA-79: "the bottom floating menu ... needs DELETE (bulk delete)
              added as one of the options." Destructive styling so it does not
              read as a peer of the two additive actions beside it. */}
          <BulkBtn
            type="button"
            danger
            disabled={deleteItems.isLoading}
            onClick={handleBulkDelete}
          >
            <DeleteOutlineRoundedIcon fontSize="small" />
            Delete
          </BulkBtn>
          <BulkClear type="button" aria-label="Clear selection" onClick={clearSelection}>
            <CloseRoundedIcon fontSize="small" />
          </BulkClear>
        </BulkBar>
      )}

      {/* QA-101 (web leg): the composer opens on the category the user is
          standing on — the active filter chip — falling back to the CAP-4
          last-picked memory on the All chip. */}
      <AddCaptureModal
        open={showAdd}
        initialType={composerTypeForFilter(filterType)}
        onClose={() => setShowAdd(false)}
      />
      {/* CAP-3: edit mode -- same composer, seeded from the item being edited.
          A separate mount from the "New capture" modal above (distinct
          open-state + prop shape) rather than overloading one instance with
          an isEditing flag threaded through the parent. */}
      <AddCaptureModal
        open={!!editItem}
        editItem={editItem}
        onClose={() => setEditItem(null)}
      />
      <AttachWheelModal
        open={!!attachPicker}
        items={attachPicker?.items}
        title={attachPicker?.title}
        onClose={() => setAttachPicker(null)}
        onAttached={() => {
          setAttachPicker(null);
          clearSelection();
        }}
      />
      <DetachWheelModal
        open={!!detachPickItem}
        item={detachPickItem}
        initialSelectAll={detachPickSelectAll}
        onClose={closeDetachPicker}
        onPick={(wheels) => {
          const item = detachPickItem;
          closeDetachPicker();
          confirmDetachFrom(item, wheels);
        }}
      />
      {/* Shared confirm dialog (redux-driven), mounted per-page like Gallery. */}
      <DeleteModal />
      {/* Create-a-Wheel wizard, mounted once per-page (WEB-035 double-mount
          regression) -- same pattern as Gallery/index.jsx. Capture has no
          other mount of it, so handleCreateNew's showCreateWheelPopup()
          dispatch above had nothing to render without this. */}
      <CreateWheelPopup />
    </Page>
  );
};

export default Capture;
